import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import AppLayout from '@/layouts/AppLayout';
import AuthLayout from '@/layouts/AuthLayout';
import LoadingScreen from '@/components/ui/LoadingScreen';
import IntroScreen from '@/components/ui/IntroScreen';
import { PageLoaderProvider } from '@/contexts/PageLoaderContext';
import type { Permission } from '@/types';

/** Returns /intro if the 24-h session flag is not yet set, otherwise /login */
function rootRedirect(): string {
  try {
    const raw = localStorage.getItem('unibm_intro_seen_v2') || sessionStorage.getItem('unibm_intro_seen_v2');
    if (raw) {
      const { ts } = JSON.parse(raw);
      if ((Date.now() - ts) / 3_600_000 < 24) return '/login';
    }
  } catch { /* ignore */ }
  return '/intro';
}

// Auth pages
const Login          = lazy(() => import('@/pages/auth/Login'));
const Register       = lazy(() => import('@/pages/auth/Register'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword  = lazy(() => import('@/pages/auth/ResetPassword'));
const CompanySetup   = lazy(() => import('@/pages/company/CompanySetup'));

// App pages
const Dashboard       = lazy(() => import('@/pages/Dashboard'));
const InvoiceList     = lazy(() => import('@/pages/invoices/InvoiceList'));
const InvoiceCreate   = lazy(() => import('@/pages/invoices/InvoiceCreate'));
const InvoiceEdit     = lazy(() => import('@/pages/invoices/InvoiceEdit'));
const InvoiceDetail   = lazy(() => import('@/pages/invoices/InvoiceDetail'));
const InvoicePreview  = lazy(() => import('@/pages/invoices/InvoicePreview'));
const CustomerList    = lazy(() => import('@/pages/customers/CustomerList'));
const CustomerCreate  = lazy(() => import('@/pages/customers/CustomerCreate'));
const CustomerDetail  = lazy(() => import('@/pages/customers/CustomerDetail'));
const CustomerEdit    = lazy(() => import('@/pages/customers/CustomerEdit'));
const ProductList     = lazy(() => import('@/pages/products/ProductList'));
const ProductCreate   = lazy(() => import('@/pages/products/ProductCreate'));
const ProductDetail   = lazy(() => import('@/pages/products/ProductDetail'));
const ProductEdit     = lazy(() => import('@/pages/products/ProductEdit'));
const PaymentList     = lazy(() => import('@/pages/payments/PaymentList'));
const PaymentDetail   = lazy(() => import('@/pages/payments/PaymentDetail'));
const Reports         = lazy(() => import('@/pages/reports/Reports'));
const SalesReport     = lazy(() => import('@/pages/reports/SalesReport'));
const GSTReport       = lazy(() => import('@/pages/reports/GSTReport'));
const CustomerReport  = lazy(() => import('@/pages/reports/CustomerReport'));
const ProductReport   = lazy(() => import('@/pages/reports/ProductReport'));
const PaymentReport   = lazy(() => import('@/pages/reports/PaymentReport'));
const UserList        = lazy(() => import('@/pages/users/UserList'));
const UserCreate      = lazy(() => import('@/pages/users/UserCreate'));
const RoleList        = lazy(() => import('@/pages/roles/RoleList'));
const AuditLogList    = lazy(() => import('@/pages/audit/AuditLogList'));
const CompanySettings = lazy(() => import('@/pages/settings/CompanySettings'));
const InvoiceSettings = lazy(() => import('@/pages/settings/InvoiceSettings'));
const TaxSettings     = lazy(() => import('@/pages/settings/TaxSettings'));
const TemplateSettings= lazy(() => import('@/pages/settings/TemplateSettings'));
const ProfileSettings = lazy(() => import('@/pages/settings/ProfileSettings'));
const UnitSettings    = lazy(() => import('@/pages/settings/UnitSettings'));
const RateListView    = lazy(() => import('@/pages/rates/RateListView'));
const RateListAdmin   = lazy(() => import('@/pages/rates/RateListAdmin'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firebaseUser, appUser, loading } = useAuthContext();
  if (loading) return <LoadingScreen />;
  if (!firebaseUser) {
    // First visit: show animated intro before landing on login
    return <Navigate to={rootRedirect()} replace />;
  }
  if (appUser?.status === 'inactive') return <Navigate to="/login" replace />;
  if (!appUser?.companyId) return <Navigate to="/setup" replace />;
  return <>{children}</>;
};

const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firebaseUser, appUser, loading } = useAuthContext();
  if (loading) return <LoadingScreen />;
  if (firebaseUser && appUser?.companyId && appUser?.status !== 'inactive') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const SetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firebaseUser, appUser, loading } = useAuthContext();
  if (loading) return <LoadingScreen />;
  if (!firebaseUser) return <Navigate to="/login" replace />;
  if (appUser?.companyId) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const PermissionRoute: React.FC<{
  permission?: Permission;
  permissions?: Permission[];
  children: React.ReactNode;
}> = ({ permission, permissions: perms, children }) => {
  const { can, canAny, isAdmin, loading } = useAuthContext();
  if (loading) return <LoadingScreen />;
  if (isAdmin) return <>{children}</>;
  if (permission && !can(permission)) return <Navigate to="/dashboard" replace />;
  if (perms && !canAny(perms)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const RateListRoute: React.FC = () => {
  const { firebaseUser, appUser, loading } = useAuthContext();
  const { companyId: routeCompanyId, listId: routeListId } = useParams<{ companyId?: string; listId?: string }>();
  const [searchParams] = useSearchParams();
  const queryCompanyId = searchParams.get('company') || searchParams.get('companyId') || searchParams.get('c');
  const queryListId = searchParams.get('list') || searchParams.get('rateListId') || searchParams.get('l');
  const targetCompanyId = routeCompanyId || queryCompanyId;
  const targetListId = routeListId || queryListId;

  if (loading) return <LoadingScreen message="loading UNIBMS Rate List" />;

  const isLoggedIn = Boolean(firebaseUser && appUser?.companyId && appUser?.status !== 'inactive');

  if (isLoggedIn && (!targetCompanyId || targetCompanyId === appUser?.companyId)) {
    return (
      <AppLayout>
        <RateListView standalone={false} listId={targetListId} />
      </AppLayout>
    );
  }

  return <RateListView standalone={true} companyId={targetCompanyId} listId={targetListId} />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <PageLoaderProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* ── Intro / Landing ── */}
            <Route path="/intro" element={<IntroScreen />} />

            {/* Rate List Short & Friendly Routes */}
            <Route path="/rates"                             element={<RateListRoute />} />
            <Route path="/rates/:companyId"                  element={<RateListRoute />} />
            <Route path="/rates/:companyId/:listId"          element={<RateListRoute />} />
            <Route path="/rate-list"                         element={<RateListRoute />} />
            <Route path="/rate-list/:companyId"              element={<RateListRoute />} />
            <Route path="/rate-list/:companyId/:listId"      element={<RateListRoute />} />
            <Route path="/ratelist"                          element={<RateListRoute />} />
            <Route path="/ratelist/:companyId"               element={<RateListRoute />} />
            <Route path="/ratelist/:companyId/:listId"       element={<RateListRoute />} />
            <Route path="/r"                                 element={<RateListRoute />} />
            <Route path="/r/:companyId"                      element={<RateListRoute />} />
            <Route path="/r/:companyId/:listId"              element={<RateListRoute />} />

            {/* Auth */}
            <Route path="/login"          element={<AuthRoute><Login /></AuthRoute>} />
            <Route path="/register"       element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password"element={<AuthRoute><Login /></AuthRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/action"    element={<ResetPassword />} />

            {/* Setup */}
            <Route path="/setup" element={<SetupRoute><CompanySetup /></SetupRoute>} />

            {/* Root – show intro or skip straight to login after first visit */}
            {/* App routes: all sub-paths (dashboard, invoices, etc.) live here */}
            <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard"               element={<Dashboard />} />
              <Route path="invoices"                element={<PermissionRoute permissions={['Invoice.View', 'Invoice.Create']}><InvoiceList /></PermissionRoute>} />
              <Route path="invoices/create"         element={<PermissionRoute permission="Invoice.Create"><InvoiceCreate /></PermissionRoute>} />
              <Route path="invoices/:id"            element={<PermissionRoute permission="Invoice.View"><InvoiceDetail /></PermissionRoute>} />
              <Route path="invoices/:id/edit"       element={<PermissionRoute permission="Invoice.Edit"><InvoiceEdit /></PermissionRoute>} />
              <Route path="invoices/:id/preview"    element={<PermissionRoute permission="Invoice.View"><InvoicePreview /></PermissionRoute>} />
              <Route path="customers"               element={<PermissionRoute permissions={['Customer.View', 'Customer.Create']}><CustomerList /></PermissionRoute>} />
              <Route path="customers/create"        element={<PermissionRoute permission="Customer.Create"><CustomerCreate /></PermissionRoute>} />
              <Route path="customers/:id"           element={<PermissionRoute permission="Customer.View"><CustomerDetail /></PermissionRoute>} />
              <Route path="customers/:id/edit"      element={<PermissionRoute permission="Customer.Edit"><CustomerEdit /></PermissionRoute>} />
              <Route path="products"                element={<PermissionRoute permissions={['Product.View', 'Product.Create']}><ProductList /></PermissionRoute>} />
              <Route path="products/create"         element={<PermissionRoute permission="Product.Create"><ProductCreate /></PermissionRoute>} />
              <Route path="products/:id"            element={<PermissionRoute permission="Product.View"><ProductDetail /></PermissionRoute>} />
              <Route path="products/:id/edit"       element={<PermissionRoute permission="Product.Edit"><ProductEdit /></PermissionRoute>} />
              <Route path="rate-list/manage"        element={<PermissionRoute permissions={['Product.Edit', 'Product.Create', 'RateList.Manage']}><RateListAdmin /></PermissionRoute>} />
              <Route path="payments"                element={<PermissionRoute permissions={['Payment.View', 'Payment.Create']}><PaymentList /></PermissionRoute>} />
              <Route path="payments/:id"            element={<PermissionRoute permission="Payment.View"><PaymentDetail /></PermissionRoute>} />
              <Route path="reports"                 element={<PermissionRoute permission="Report.View"><Reports /></PermissionRoute>} />
              <Route path="reports/sales"           element={<PermissionRoute permission="Report.View"><SalesReport /></PermissionRoute>} />
              <Route path="reports/gst"             element={<PermissionRoute permission="Report.View"><GSTReport /></PermissionRoute>} />
              <Route path="reports/customers"       element={<PermissionRoute permission="Report.View"><CustomerReport /></PermissionRoute>} />
              <Route path="reports/products"        element={<PermissionRoute permission="Report.View"><ProductReport /></PermissionRoute>} />
              <Route path="reports/payments"        element={<PermissionRoute permission="Report.View"><PaymentReport /></PermissionRoute>} />
              <Route path="users"                   element={<PermissionRoute permission="User.Manage"><UserList /></PermissionRoute>} />
              <Route path="users/create"            element={<PermissionRoute permission="User.Manage"><UserCreate /></PermissionRoute>} />
              <Route path="roles"                   element={<PermissionRoute permission="Role.Manage"><RoleList /></PermissionRoute>} />
              <Route path="audit-logs"              element={<PermissionRoute permission="AuditLog.View"><AuditLogList /></PermissionRoute>} />
              <Route path="settings/company"        element={<PermissionRoute permission="Settings.Manage"><CompanySettings /></PermissionRoute>} />
              <Route path="settings/invoice"        element={<PermissionRoute permission="Settings.Manage"><InvoiceSettings /></PermissionRoute>} />
              <Route path="settings/tax"            element={<PermissionRoute permission="Settings.Manage"><TaxSettings /></PermissionRoute>} />
              <Route path="settings/templates"      element={<PermissionRoute permission="Template.Manage"><TemplateSettings /></PermissionRoute>} />
              <Route path="settings/profile"        element={<ProfileSettings />} />
              <Route path="settings/units"          element={<PermissionRoute permission="Settings.Manage"><UnitSettings /></PermissionRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </PageLoaderProvider>
    </BrowserRouter>
  );
};

export default App;
